<template>
  <b-card no-body class="compact-feed-card h-100">
    <!-- Compact Header -->
    <b-card-header class="compact-header py-2">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h6 class="mb-0 font-weight-bold">Live Feed</h6>
          <small class="text-muted">Real-time activity</small>
        </div>
        <div class="mini-stats">
          <b-badge variant="primary" class="stat-badge">{{ recentEvents.length }}</b-badge>
          <b-badge variant="info" class="stat-badge ml-1">{{ eventStats.detected }}</b-badge>
          <b-badge variant="success" class="stat-badge ml-1">{{ eventStats.confirmed }}</b-badge>
        </div>
      </div>
    </b-card-header>

    <!-- Quick Filters -->
    <div class="quick-filters px-3 py-2 border-bottom">
      <div class="d-flex">
        <b-button-group size="sm" class="flex-grow-1">
          <b-button 
            :variant="localFilters.eventType === null ? 'primary' : 'outline-primary'"
            @click="setFilter('eventType', null)"
            class="px-2"
          >
            All
          </b-button>
          <b-button 
            :variant="localFilters.eventType === 'beacon_detected' ? 'info' : 'outline-info'"
            @click="setFilter('eventType', 'beacon_detected')"
            class="px-2"
          >
            <i class="fas fa-broadcast-tower"></i>
          </b-button>
          <b-button 
            :variant="localFilters.eventType === 'confirm_visit' ? 'success' : 'outline-success'"
            @click="setFilter('eventType', 'confirm_visit')"
            class="px-2"
          >
            <i class="fas fa-check"></i>
          </b-button>
        </b-button-group>
        <b-button 
          v-if="hasActiveFilters"
          size="sm" 
          variant="link" 
          class="text-danger ml-2"
          @click="clearAllFilters"
        >
          Clear
        </b-button>
      </div>
    </div>

    <!-- Events List - Super Compact -->
    <div class="events-list">
      <!-- Empty States -->
      <div v-if="filteredEvents.length === 0 && recentEvents.length > 0" class="empty-state text-center py-4">
        <i class="fas fa-filter fa-lg text-muted mb-2"></i>
        <p class="text-muted mb-0 small">No matching events</p>
      </div>

      <div v-if="recentEvents.length === 0" class="empty-state text-center py-4">
        <div class="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="text-muted mb-0 small">Waiting...</p>
      </div>

      <!-- Event Items -->
      <div 
        v-for="(event, index) in filteredEvents"
        :key="event.id"
        class="event-item"
        :class="{'confirmed': event.event_type === 'confirm_visit', 'detected': event.event_type === 'beacon_detected'}"
      >
        <div class="event-number">{{ index + 1 }}</div>
        <div class="event-content">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1 mr-2">
              <div class="event-main">
                <!-- CORRECTED SYMBOLS: -->
                <b-badge 
                  :variant="getEventBadgeVariant(event)"
                  class="mr-1 event-type"
                >
                  <i :class="getEventIcon(event)"></i>
                </b-badge>
                <span class="patient-name">{{ truncateText(event.patient || 'Unknown', 15) }}</span>
              </div>
              <div class="event-details mt-1">
                <small class="text-muted d-block">
                  <i class="fas fa-user-nurse mr-1"></i>{{ truncateText(event.assigned_nurse, 12) }}
                </small>
              </div>
            </div>
            <div class="event-meta text-right">
              <div v-if="event.event_type === 'beacon_detected' && !event.confirmed" class="mb-1">
                <b-button size="sm" variant="outline-success" class="py-0 px-2 btn-xs">
                  <i class="fas fa-check fa-xs"></i>
                </b-button>
              </div>
              <small class="text-muted d-block">{{ formatTimeAgoShort(event.timestamp) }}</small>
            </div>
          </div>
          
          <!-- Bottom Row -->
          <div class="event-footer mt-1">
            <small class="text-muted mr-2">
              <i class="fas fa-mobile-alt mr-1"></i>{{ truncateText(event.device_id, 10) }}
            </small>
            <div v-if="event.rssi" class="signal-indicator d-inline-block">
              <i class="fas fa-signal mr-1"></i>
              <small>{{ Math.abs(event.rssi) }}dBm</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Simple Footer -->
    <div class="card-footer py-1 border-top">
      <small class="text-muted">
        {{ filteredEvents.length }} of {{ recentEvents.length }}
        <b-button 
          v-if="filteredEvents.length >= localFilters.maxEvents"
          size="sm" 
          variant="link" 
          class="float-right p-0"
          @click="loadMore"
        >
          More
        </b-button>
      </small>
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'LiveActivityFeed',
  props: {
    recentEvents: {
      type: Array,
      default: () => []
    },
    activeDevicesList: {
      type: Array,
      default: () => []
    },
    liveFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localFilters: {
        eventType: null,
        deviceId: null,
        maxEvents: 10,
        ...this.liveFilters
      }
    };
  },
  computed: {
    filteredEvents() {
      let events = this.recentEvents;
      
      if (this.localFilters.eventType) {
        events = events.filter(e => e.event_type === this.localFilters.eventType);
      }
      
      if (this.localFilters.deviceId) {
        events = events.filter(e => e.device_id === this.localFilters.deviceId);
      }
      
      return events.slice(0, this.localFilters.maxEvents);
    },
    
    eventStats() {
      return {
        detected: this.recentEvents.filter(e => e.event_type === 'beacon_detected').length,
        confirmed: this.recentEvents.filter(e => e.event_type === 'confirm_visit').length,
        total: this.recentEvents.length
      };
    },
    
    hasActiveFilters() {
      return this.localFilters.eventType || this.localFilters.deviceId;
    }
  },
  methods: {
    // Filter methods
    setFilter(key, value) {
      this.localFilters[key] = value;
      this.updateFilters();
    },
    
    updateFilters() {
      this.$emit('update-filters', this.localFilters);
    },
    
    clearFilter(filterName) {
      this.localFilters[filterName] = null;
      this.updateFilters();
      this.$emit('clear-filter', filterName);
    },
    
    clearAllFilters() {
      this.localFilters = {
        eventType: null,
        deviceId: null,
        maxEvents: 10
      };
      this.updateFilters();
      this.$emit('clear-all-filters');
    },
    
    loadMore() {
      this.localFilters.maxEvents += 5;
      this.updateFilters();
    },
    
    // Helper methods
    getEventBadgeVariant(event) {
      if (event.event_type === 'confirm_visit') return 'success';
      if (event.event_type === 'beacon_detected') {
        return event.confirmed ? 'success' : 'info';
      }
      return 'primary';
    },
    
    // CORRECTED ICONS: More appropriate symbols
    getEventIcon(event) {
      if (event.event_type === 'confirm_visit') {
        return 'fas fa-check-circle';
      } else if (event.event_type === 'beacon_detected') {
        return 'fas fa-broadcast-tower';
      }
      return 'fas fa-circle';
    },
    
    // Formatting methods
    formatTimeAgoShort(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const seconds = Math.floor((Date.now() - date) / 1000);

      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
      return `${Math.floor(seconds / 86400)}d`;
    },
    
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    }
  },
  watch: {
    liveFilters: {
      handler(newFilters) {
        this.localFilters = { ...this.localFilters, ...newFilters };
      },
      deep: true
    }
  },
  emits: ['update-filters', 'clear-filter', 'clear-all-filters']
}
</script>

<style scoped>
.compact-feed-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* Header */
.compact-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  padding: 0.5rem 1rem;
}

.compact-header h6 {
  font-size: 0.95rem;
  color: #333;
}

.mini-stats {
  display: flex;
}

.stat-badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  min-width: 24px;
  text-align: center;
}

/* Quick Filters */
.quick-filters {
  background: #fff;
}

.quick-filters .btn-group .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

/* Events List */
.events-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  min-height: 200px;
  max-height: 350px;
}

/* Event Item */
.event-item {
  display: flex;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  transition: all 0.2s;
  font-size: 0.85rem;
}

.event-item:hover {
  background: #f8f9fa;
  border-color: #ddd;
}

.event-item.confirmed {
  border-left: 3px solid #28a745;
}

.event-item.detected {
  border-left: 3px solid #17a2b8;
}

.event-number {
  width: 22px;
  height: 22px;
  background: #4a6ee0;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.event-item.confirmed .event-number {
  background: #28a745;
}

.event-item.detected .event-number {
  background: #17a2b8;
}

.event-content {
  flex: 1;
  min-width: 0;
}

.event-main {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}

/* Updated Event Type Badge with icons */
.event-type {
  width: 20px;
  height: 20px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.event-type i {
  font-size: 0.7rem;
}

.patient-name {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
  margin-left: 0.25rem;
}

.event-details small {
  font-size: 0.8rem;
}

.event-meta {
  flex-shrink: 0;
}

.event-meta .btn {
  font-size: 0.7rem;
  padding: 0.1rem 0.3rem;
}

.event-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Signal Indicator */
.signal-indicator {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
}

.signal-indicator i {
  color: #6c757d;
  font-size: 0.7rem;
}

/* Empty States */
.empty-state {
  color: #6c757d;
}

.empty-state i {
  opacity: 0.5;
}

.empty-state p {
  font-size: 0.85rem;
}

/* Custom Scrollbar */
.events-list::-webkit-scrollbar {
  width: 4px;
}

.events-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.events-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.events-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive */
@media (max-width: 768px) {
  .events-list {
    max-height: 300px;
  }
  
  .patient-name {
    font-size: 0.85rem;
  }
}
</style>